import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Coins,
  ChevronDown,
  Menu,
  X,
  GraduationCap,
  Calculator,
  Shield,
  TrendingUp,
  PiggyBank,
  BookOpen,
  Smartphone,
  Building,
  CreditCard,
  DollarSign,
  Target,
  PieChart,
  Calendar,
  FileText,
  BarChart3
} from 'lucide-react';
import { clsx } from 'clsx';

interface NavigationItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  description?: string;
}

interface NavigationCategory {
  label: string;
  items: NavigationItem[];
  icon: React.ReactNode;
}

const navigationCategories: NavigationCategory[] = [
  {
    label: 'Educação Financeira',
    icon: <GraduationCap className="w-5 h-5" />,
    items: [
      {
        label: 'Gerenciar Dívidas',
        href: '/dividas',
        icon: <Calculator className="w-4 h-4" />,
        description: 'Estratégias para sair do vermelho'
      },
      {
        label: 'Dicas de Economia',
        href: '/economia',
        icon: <PiggyBank className="w-4 h-4" />,
        description: 'Como economizar no dia a dia'
      },
      {
        label: 'Planejamento Financeiro',
        href: '/planejamento',
        icon: <Target className="w-4 h-4" />,
        description: 'Organize suas finanças pessoais'
      }
    ]
  },
  {
    label: 'Investimentos',
    icon: <TrendingUp className="w-5 h-5" />,
    items: [
      {
        label: 'Guia para Iniciantes',
        href: '/investimentos',
        icon: <BookOpen className="w-4 h-4" />,
        description: 'Comece a investir com segurança'
      },
      {
        label: 'Tesouro Direto',
        href: '/tesouro-direto',
        icon: <Shield className="w-4 h-4" />,
        description: 'Investimentos do governo federal'
      },
      {
        label: 'Fundos Imobiliários',
        href: '/fiis',
        icon: <Building className="w-4 h-4" />,
        description: 'Ganhe com aluguel sem comprar imóvel'
      },
      {
        label: 'Criptomoedas',
        href: '/criptomoedas',
        icon: <DollarSign className="w-4 h-4" />,
        description: 'Bitcoin e outras moedas digitais'
      }
    ]
  },
  {
    label: 'Banco & Cartões',
    icon: <CreditCard className="w-5 h-5" />,
    items: [
      {
        label: 'Bancos Digitais',
        href: '/bancos-digitais',
        icon: <Smartphone className="w-4 h-4" />,
        description: 'Nubank, Inter, C6 Bank e outros'
      },
      {
        label: 'Cartão de Crédito',
        href: '/cartao-credito',
        icon: <CreditCard className="w-4 h-4" />,
        description: 'Como escolher e usar melhor'
      },
      {
        label: 'PIX',
        href: '/pix',
        icon: <Shield className="w-4 h-4" />,
        description: 'Sistema de pagamento instantâneo'
      }
    ]
  },
  {
    label: 'Imóveis',
    icon: <Building className="w-5 h-5" />,
    items: [
      {
        label: 'Financiamento',
        href: '/financiamento',
        icon: <Calculator className="w-4 h-4" />,
        description: 'Como financiar seu imóvel'
      },
      {
        label: 'Aluguel vs Compra',
        href: '/aluguel-vs-compra',
        icon: <PieChart className="w-4 h-4" />,
        description: 'Qual opção é melhor para você?'
      }
    ]
  },
  {
    label: 'Impostos & IR',
    icon: <FileText className="w-5 h-5" />,
    items: [
      {
        label: 'Declaração de IR',
        href: '/imposto-renda',
        icon: <Calendar className="w-4 h-4" />,
        description: 'Como declarar investimentos'
      },
      {
        label: 'Isenções',
        href: '/isencoes',
        icon: <Shield className="w-4 h-4" />,
        description: 'Quem tem direito a isenção?'
      }
    ]
  },
  {
    label: 'Ferramentas',
    icon: <Calculator className="w-5 h-5" />,
    items: [
      {
        label: 'Calculadoras',
        href: '/calculadoras',
        icon: <Calculator className="w-4 h-4" />,
        description: 'Todas as calculadoras financeiras'
      },
      {
        label: 'Planilhas',
        href: '/planilhas',
        icon: <FileText className="w-4 h-4" />,
        description: 'Downloads gratuitos'
      }
    ]
  },
  {
    label: 'Recursos',
    icon: <BarChart3 className="w-5 h-5" />,
    items: [
      {
        label: 'Blog',
        href: '/blog',
        icon: <BookOpen className="w-4 h-4" />,
        description: 'Dicas semanais de finanças'
      },
      {
        label: 'Mercado',
        href: '/mercado',
        icon: <BarChart3 className="w-4 h-4" />,
        description: 'Análises e tendências'
      }
    ]
  }
];

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const handleDropdownToggle = (categoryLabel: string) => {
    setActiveDropdown(activeDropdown === categoryLabel ? null : categoryLabel);
  };

  const handleNavigationClick = () => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-white/20 shadow-sm sticky top-0 z-50 transition-all duration-300">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="bg-primary-50 p-2 rounded-xl group-hover:bg-primary-100 transition-colors duration-300">
              <Coins className="w-8 h-8 text-primary-600" />
            </div>
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-700 to-primary-500">O Teu Dinheiro</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2" ref={dropdownRef}>
            {navigationCategories.map((category) => (
              <div key={category.label} className="relative group">
                <button
                  onClick={() => handleDropdownToggle(category.label)}
                  className={clsx(
                    "flex items-center space-x-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border border-transparent",
                    activeDropdown === category.label
                      ? "bg-primary-50 text-primary-700 border-primary-100"
                      : "text-gray-600 hover:text-primary-600 hover:bg-gray-50"
                  )}
                  aria-expanded={activeDropdown === category.label}
                  aria-haspopup="true"
                >
                  {category.icon}
                  <span>{category.label}</span>
                  <ChevronDown className={clsx(
                    "w-4 h-4 transition-transform duration-200",
                    activeDropdown === category.label && "rotate-180"
                  )} aria-hidden="true" />
                </button>

                {/* Dropdown Menu */}
                {activeDropdown === category.label && (
                  <div className="absolute top-full left-0 mt-4 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-100/50 py-4 animate-slide-down z-50 overflow-hidden ring-1 ring-black/5">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                      <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                        {category.icon}
                        <span>{category.label}</span>
                      </h3>
                    </div>
                    <div className="p-2">
                      {category.items.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={handleNavigationClick}
                          className={clsx(
                            "flex items-start space-x-3 px-4 py-3 rounded-xl transition-all duration-200 group/item",
                            isActive(item.href)
                              ? "bg-primary-50 text-primary-700"
                              : "hover:bg-gray-50 text-gray-700"
                          )}
                        >
                          <div className={clsx(
                            "p-2 rounded-lg transition-colors duration-200",
                            isActive(item.href) ? "bg-white shadow-sm text-primary-600" : "bg-gray-100 text-gray-500 group-hover/item:bg-white group-hover/item:shadow-sm group-hover/item:text-primary-600"
                          )}>
                            {item.icon}
                          </div>
                          <div className="flex-1">
                            <div className={clsx(
                              "font-medium text-sm",
                              isActive(item.href) ? "text-primary-700" : "text-gray-900"
                            )}>
                              {item.label}
                            </div>
                            {item.description && (
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1 group-hover/item:text-gray-600">{item.description}</p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div id="mobile-menu" className="lg:hidden border-t border-gray-100 py-4 animate-slide-down h-[calc(100vh-5rem)] overflow-y-auto">
            <nav className="space-y-6 px-2 pb-20">
              {navigationCategories.map((category) => (
                <div key={category.label} className="space-y-2">
                  <div className="flex items-center space-x-2 px-4 py-2 text-sm font-bold text-primary-700 uppercase tracking-wider bg-primary-50/50 rounded-lg">
                    {category.icon}
                    <span>{category.label}</span>
                  </div>
                  <div className="space-y-1 pl-2">
                    {category.items.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={handleNavigationClick}
                        className={clsx(
                          "flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 active:scale-95",
                          isActive(item.href)
                            ? "bg-primary-50 text-primary-700 shadow-sm"
                            : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        <div className={clsx(
                          "p-2 rounded-lg",
                          isActive(item.href) ? "bg-white text-primary-600" : "bg-gray-100 text-gray-500"
                        )}>
                          {item.icon}
                        </div>
                        <div>
                          <div className="font-medium">{item.label}</div>
                          {item.description && (
                            <p className="text-xs text-gray-500">{item.description}</p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
